import {
    createContext,
    MutableRefObject,
    ReactNode,
    useCallback,
    useContext,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import type { OnChangeFn } from "@tanstack/react-table";

export interface SpreadsheetColumnResizeState {
    columnSizing: Record<string, number>;
    setColumnSizing: OnChangeFn<Record<string, number>>;
    userResizedColumnIdsRef: MutableRefObject<Set<string>>;
    activelyResizingColumnId: string | null;
    claimActivelyResizingColumnId: (columnId: string) => void;
    releaseActivelyResizingColumnId: (columnId: string) => void;
    setLiveGridTemplateColumns: (gridTemplateColumns: string) => void;
    columnLayoutGeneration: number;
}

const SpreadsheetColumnResizeContext =
    createContext<SpreadsheetColumnResizeState | null>(null);
const LiveGridTemplateColumnsContext = createContext<string | undefined>(
    undefined,
);

export function SpreadsheetColumnResizeProvider({
    columnLayoutKey,
    retainedColumnIds,
    children,
}: {
    columnLayoutKey?: string;
    retainedColumnIds?: string[];
    children: ReactNode;
}) {
    const [columnSizing, setColumnSizing] = useState<Record<string, number>>(
        {},
    );
    const userResizedColumnIdsRef = useRef<Set<string>>(new Set());
    const [activelyResizingColumnId, setActivelyResizingColumnId] = useState<
        string | null
    >(null);
    const [liveGridTemplateColumns, setLiveGridTemplateColumns] = useState<
        string | undefined
    >();
    const previousColumnLayoutKeyRef = useRef<string | undefined>(undefined);
    const [columnLayoutGeneration, setColumnLayoutGeneration] = useState(0);
    useLayoutEffect(() => {
        if (!columnLayoutKey) {
            return;
        }
        if (previousColumnLayoutKeyRef.current === undefined) {
            previousColumnLayoutKeyRef.current = columnLayoutKey;
            return;
        }
        if (previousColumnLayoutKeyRef.current === columnLayoutKey) {
            return;
        }
        previousColumnLayoutKeyRef.current = columnLayoutKey;
        setColumnSizing({});
        userResizedColumnIdsRef.current = new Set();
        setActivelyResizingColumnId(null);
        setLiveGridTemplateColumns(undefined);
        setColumnLayoutGeneration((generation) => generation + 1);
    }, [columnLayoutKey]);

    const retainedColumnIdsKey = retainedColumnIds?.join("\0");
    useLayoutEffect(() => {
        if (retainedColumnIdsKey === undefined) {
            return;
        }
        const columnIds = new Set(retainedColumnIdsKey.split("\0"));
        userResizedColumnIdsRef.current = new Set(
            [...userResizedColumnIdsRef.current].filter((columnId) =>
                columnIds.has(columnId),
            ),
        );
        setColumnSizing((prev) => {
            const retainedEntries = Object.entries(prev).filter(([columnId]) =>
                columnIds.has(columnId),
            );
            const hasRemovals =
                retainedEntries.length !== Object.keys(prev).length;
            return hasRemovals ? Object.fromEntries(retainedEntries) : prev;
        });
    }, [retainedColumnIdsKey]);

    const claimActivelyResizingColumnId = useCallback((columnId: string) => {
        setActivelyResizingColumnId(columnId);
    }, []);
    const releaseActivelyResizingColumnId = useCallback((columnId: string) => {
        setActivelyResizingColumnId((current) =>
            current === columnId ? null : current,
        );
    }, []);
    const value = useMemo(
        () => ({
            columnSizing,
            setColumnSizing,
            userResizedColumnIdsRef,
            activelyResizingColumnId,
            claimActivelyResizingColumnId,
            releaseActivelyResizingColumnId,
            setLiveGridTemplateColumns,
            columnLayoutGeneration,
        }),
        [
            columnSizing,
            activelyResizingColumnId,
            claimActivelyResizingColumnId,
            releaseActivelyResizingColumnId,
            columnLayoutGeneration,
        ],
    );
    return (
        <SpreadsheetColumnResizeContext.Provider value={value}>
            <LiveGridTemplateColumnsContext.Provider
                value={liveGridTemplateColumns}
            >
                {children}
            </LiveGridTemplateColumnsContext.Provider>
        </SpreadsheetColumnResizeContext.Provider>
    );
}

export function SpreadsheetColumnResizeScope({
    retainedColumnIds,
    children,
}: {
    retainedColumnIds: string[];
    children: ReactNode;
}) {
    const ancestorResizeState = useContext(SpreadsheetColumnResizeContext);
    if (ancestorResizeState) {
        return children;
    }
    return (
        <SpreadsheetColumnResizeProvider retainedColumnIds={retainedColumnIds}>
            {children}
        </SpreadsheetColumnResizeProvider>
    );
}

export function useSpreadsheetColumnResizeContext(): SpreadsheetColumnResizeState {
    const resizeState = useContext(SpreadsheetColumnResizeContext);
    if (!resizeState) {
        throw new Error(
            "useSpreadsheetColumnResizeContext must be used within SpreadsheetColumnResizeProvider",
        );
    }
    return resizeState;
}

export function useSharedSpreadsheetGridTemplate(
    defaultGridTemplateColumns: string,
): string {
    const liveGridTemplateColumns = useContext(LiveGridTemplateColumnsContext);
    return liveGridTemplateColumns ?? defaultGridTemplateColumns;
}

export function SpreadsheetGridTemplate({
    defaultGridTemplateColumns,
    className,
    children,
}: {
    defaultGridTemplateColumns: string;
    className?: string;
    children: ReactNode;
}) {
    const gridTemplateColumns = useSharedSpreadsheetGridTemplate(
        defaultGridTemplateColumns,
    );
    return (
        <div className={className} style={{ gridTemplateColumns }}>
            {children}
        </div>
    );
}
